import { useCallback } from 'react';
import { getTopicDetails } from 'src/apis/helpdesk/topic.api';
import { TopicDetailSendingStatus } from 'src/constants/topic.enum';
import { useChatStore } from 'src/stores/chatStore';
import { ITopicDetail } from 'src/types/helpdesk/topic.type';
import { cleanupOldData, getStorageStats, saveMessagesToDB } from 'src/utils/chatFileDB';

export function useChatFileService() {
  const preloadRoomInfo = useCallback(async (roomId: string) => {
    // ✅ Dùng store method thay vì manual check
    const store = useChatStore.getState();
    await store.loadRoomInfo(roomId);
  }, []);

  const preloadChatMessages = useCallback(async (roomId: string) => {
    const store = useChatStore.getState();
    const roomInfo = store.rooms[roomId];
    let cached = roomInfo.allMessages;

    try {
      // ✅ STEP 1: Load from IndexedDB first if no memory cache
      if (!cached || cached.length === 0) {
        try {
          // Assume you have this function in chatFileDB.ts
          const { getMessagesFromDB } = await import('src/utils/chatFileDB');
          const dbMessages = await getMessagesFromDB(roomId);

          if (dbMessages && dbMessages.length > 0) {
            // ✅ Show cached messages immediately
            store.setMessages(roomId, dbMessages);
            cached = dbMessages; // Update reference for merging
          }
        } catch (dbError) {
          console.warn('⚠️ Failed to load from IndexedDB:', dbError);
        }
      }

      // ✅ STEP 2: Always call API to get latest messages
      const now = new Date();
      const lastLoaded = roomInfo?.lastLoadedAt ? new Date(roomInfo.lastLoadedAt) : undefined;

      // ✅ Calculate fromDate - recent activity needs full refresh
      const isRecentLoad = lastLoaded && now.getTime() - lastLoaded.getTime() < 5 * 60 * 1000; // 5 minutes
      const fromDate = isRecentLoad ? undefined : lastLoaded;

      const response = await getTopicDetails(roomId, false, fromDate);

      if (response.data && response.data.length > 0) {
        // const newGrouped = groupMessages(response.data);

        let finalMessages: ITopicDetail[] = [...response.data];

        // ✅ STEP 3: Merge cached + API messages (always merge unless full refresh)
        if (cached && cached.length > 0) {
          // ✅ Deduplicate - Priority: Keep failed > newer > older
          finalMessages = [...cached, ...response.data].filter((message, index, array) => {
            const duplicateIndex = array.findIndex((existing, existingIndex) => {
              if (existingIndex >= index) return false; // Only check previous items

              return (
                existing.id === message.id ||
                (existing.tempId && existing.tempId === message.tempId) ||
                existing.tempId === message.id ||
                message.tempId === existing.id
              );
            });

            if (duplicateIndex !== -1) {
              const existing = array[duplicateIndex];

              // ✅ Priority 1: Keep failed messages over successful ones
              if (
                message.sendingStatus === TopicDetailSendingStatus.Failed &&
                existing.sendingStatus !== TopicDetailSendingStatus.Failed
              ) {
                return true; // Keep current failed
              }

              if (
                existing.sendingStatus === TopicDetailSendingStatus.Failed &&
                message.sendingStatus !== TopicDetailSendingStatus.Failed
              ) {
                return false; // Skip current, keep existing failed
              }

              // ✅ Priority 2: Keep newer messages (by lastModifiedDate or createdDate)
              const existingDate = new Date(existing.lastModifiedDate || existing.createdDate);
              const currentDate = new Date(message.lastModifiedDate || message.createdDate);

              return currentDate > existingDate; // Keep newer
            }

            return true; // Not duplicate
          });

          // ✅ Sort and regroup
          // finalMessages.sort((a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime());
        }
        const lastItem = finalMessages?.[finalMessages?.length - 1];
        console.log(lastItem);

        // ✅ STEP 4: Update store and IndexedDB
        if (!lastItem?.tempId && !lastItem?.id) {
          return;
        }
        store.setMessages(roomId, finalMessages);
        store.setVisibleById(roomId, String(lastItem?.tempId || lastItem?.id));
        store.setLoading(roomId, false);

        // ✅ Save merged result to IndexedDB
        try {
          await saveMessagesToDB(roomId, finalMessages);
        } catch (saveError) {
          console.error('❌ Failed to save merged messages:', saveError);
        }
      }
    } catch (error) {
      console.error('❌ Failed to load topic messages:', error);

      // ✅ Fallback: Use cached messages if API fails
      if (cached && cached.length > 0) {
        store.setMessages(roomId, cached);
      }
    }
  }, []);

  const preloadRoom = useCallback(
    async (topicId: string) => {
      // 1. Load room info (members, basic data)
      await preloadRoomInfo(topicId);
      // 2. Load messages
      await preloadChatMessages(topicId);
    },
    [preloadRoomInfo, preloadChatMessages]
  );

  // Cập nhật temp message thành final message
  const updateTempMessage = useCallback((tempId: string, finalMessage: ITopicDetail) => {
    const store = useChatStore.getState();

    const topics = Object.values(store.rooms);

    topics.forEach((topic) => {
      const messages = topic.allMessages || [];

      // Check xem topic này có temp message không
      const pendingMessage = topic.pendingMessages.find((msg: ITopicDetail) => msg.tempId === tempId);

      if (pendingMessage) {
        // Update messages trong topic này
        const updatedMessage = {
          ...pendingMessage, // Keep existing data
          ...finalMessage, // Override with new data
          id: finalMessage.id || pendingMessage.id,
          sendingStatus: finalMessage.sendingStatus || TopicDetailSendingStatus.Sent,
          filePaths:
            finalMessage.filePaths?.map((file) => ({ ...file })) ||
            pendingMessage.filePaths?.map((file) => ({ ...file })) ||
            []
        };

        const updatedMessageList = [...messages, updatedMessage];

        const lastItem = updatedMessageList?.[updatedMessageList?.length - 1];

        // Update store
        if (!lastItem?.tempId && !lastItem?.id) {
          return;
        }
        console.log(String(lastItem?.tempId || lastItem?.id));

        store.removePendingMessage(topic?.id, tempId);
        store.updateMessage(topic?.id, tempId, updatedMessage);
        store.setVisibleById(topic?.id, String(lastItem?.tempId || lastItem?.id));

        // Save to IndexedDB async (không block)
        saveMessagesToDB(topic?.id, [...messages, updatedMessage])
          .then(() => {})
          .catch((error) => {
            console.error('❌ Failed to save updated message to IndexedDB:', error);
          });

        return true; // Found and updated
      }

      return false;
    });
  }, []);

  // Xử lý khi thêm tin nhắn mới từ khung chat
  const handleAddNewMessage = useCallback(async (message: ITopicDetail) => {
    const store = useChatStore.getState();
    const topicId = message.topicId;
    const currentRoom = store.rooms[topicId];
    const pendingMessages = currentRoom.pendingMessages || [];

    const isPendingMessage = pendingMessages.some((msg: ITopicDetail) => msg.tempId === message.tempId);

    if (isPendingMessage) {
      return;
    }

    const newMessages = [...currentRoom.allMessages, message];
    const lastItem = newMessages?.[newMessages?.length - 1];

    if (!lastItem?.tempId && !lastItem?.id) {
      return;
    }
    store.setMessages(topicId, [...currentRoom?.allMessages, message]);
    store.setVisibleById(topicId, String(lastItem?.tempId || lastItem?.id));
    store.addPendingMessage(topicId, message);
    try {
      saveMessagesToDB(topicId, [...currentRoom?.allMessages, message]);
    } catch (error) {
      console.error('❌ Failed to save messages to IndexedDB:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Xử lý khi nhận message mới từ SignalR
  const handleAddMessageFromSignR = useCallback((message: ITopicDetail) => {
    const store = useChatStore.getState();
    const topicId = message.topicId;
    const currentRoom = store.rooms[topicId];
    const messages = currentRoom.allMessages || [];
    const pendingMessages = currentRoom.pendingMessages || [];

    const isPendingMessage = pendingMessages.some((msg: ITopicDetail) => msg.tempId === message.tempId);

    if (isPendingMessage) {
      return;
    }

    const newMessages = [...messages, message];

    store.setMessages(topicId, newMessages);

    const isAtBottom = currentRoom.isAtBottom;
    if (!message.id) return;
    console.log(isAtBottom);

    if (isAtBottom) {
      // Nếu đang ở bottom, hiển thị luôn message mới (scroll)
      store.setVisibleById(topicId, message.id);
      const container = document.getElementById('topic-conversation-content');
      setTimeout(() => {
        if (container) container.scrollTop = container.scrollHeight - container.clientHeight;
      }, 10);
    } else {
      // Nếu không ở bottom, chỉ append 1 message (KHÔNG scroll)
      store.appendMessages(topicId, 1);
      store.updateUnReadMessages(topicId, 1);
    }

    try {
      saveMessagesToDB(topicId, newMessages);
    } catch (error) {
      console.error('❌ Failed to save messages to IndexedDB:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Nhảy đến message cụ thể
  const jumpToMessage = useCallback((topicId: string, msgId: string) => {
    const store = useChatStore.getState();
    const currentRoom = store.rooms[topicId];
    // Tìm index của message
    const idx = currentRoom?.allMessages?.findIndex((m) => m.id === msgId);
    if (idx < 0) return; // Không có

    // Hiển thị cửa sổ chứa message đó
    store.setVisibleById(topicId, msgId); // truyền đúng id nếu hàm nhận id!

    // Optional: focus vào message nếu có state focus
    store.focusToMessage(topicId, msgId);

    // Sau khi visibleMessages update, scroll vào message đó
    setTimeout(() => {
      const el = document.querySelector(`[data-id='${msgId}']`);
      if (el) el.scrollIntoView({ behavior: 'auto', block: 'center' });
    }, 100);

    setTimeout(() => {
      store.focusToMessage(topicId); // Clear focus after a short delay
    }, 3000);
  }, []);

  const initializeApp = useCallback(async () => {
    console.log('🚀 Initializing app with hybrid storage...');

    const store = useChatStore.getState();

    // ✅ Wait for persist hydration
    if (!store._hasHydrated) {
      console.log('⏳ Waiting for persist hydration...');
      // Could add a timeout here if needed
      return;
    }

    // ✅ Initialize session (already implemented in store)
    await store.initializeSession();

    // ✅ Cleanup old IndexedDB data
    try {
      await cleanupOldData();

      const stats = await getStorageStats();
      console.log('📊 Storage stats after cleanup:', stats);
    } catch (error) {
      console.error('⚠️ Storage cleanup failed:', error);
    }

    console.log('✅ App initialization completed');
  }, []);

  function clearAllIndexedDB() {
    const dbName = 'chat-cache';
    const deleteRequest = indexedDB.deleteDatabase(dbName);

    deleteRequest.onsuccess = () => {
      console.log(`✅ IndexedDB "${dbName}" deleted successfully`);
    };

    deleteRequest.onerror = (e) => {
      console.error(`❌ Failed to delete IndexedDB "${dbName}"`, e);
    };

    deleteRequest.onblocked = () => {
      console.warn(`⚠️ Delete blocked, please close other tabs using this DB.`);
    };
  }

  return {
    preloadRoomInfo,
    preloadChatMessages,
    preloadRoom,
    handleAddNewMessage,
    handleAddMessageFromSignR,
    jumpToMessage,
    updateTempMessage,
    clearAllIndexedDB,
    initializeApp
  };
}
